'use client'

import { cn } from '@/util/cn'
import { usePathname, useSearchParams } from 'next/navigation'
import { useRef, useState } from 'react'
import { useClickAway } from 'react-use'
import Radio from 'ui/src/components/controls/radio'

type Option = {
    /** Locale id submitted to /api/locale. */
    value: string
    /** The language's own name, so it is readable to the people who want it. */
    label: string
}

type Props = {
    options: Option[]
    activeLocale: string
    /** Accessible name for the control, already translated. */
    label: string
}

/**
 * The persistent language switcher, in the site footer on every page.
 *
 * It opens upward because it sits at the bottom of the document. It posts to
 * /api/locale rather than using a Server Action: an action posts to the current
 * URL, and this renders on pages served by Django as well as by Next, which
 * would reject the POST.
 */
const FooterLocaleSwitcher = ({ options, activeLocale, label }: Props) => {
    const [isOpen, setIsOpen] = useState(false)
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const ref = useRef(null)
    const formRef = useRef<HTMLFormElement>(null)
    const localeInputRef = useRef<HTMLInputElement>(null)

    useClickAway(ref, () => setIsOpen(false))

    const active = options.find((option) => option.value === activeLocale) ?? options[0]

    // Come back to the page they were reading, not the homepage. The endpoint
    // re-validates this, so a tampered value cannot redirect off-site.
    const query = searchParams.toString()
    const returnPath = `${pathname || '/'}${query ? `?${query}` : ''}`

    const choose = (value: string) => {
        const localeInput = localeInputRef.current

        if (!localeInput || value === activeLocale) {
            setIsOpen(false)
            return
        }

        localeInput.value = value
        formRef.current?.requestSubmit()
    }

    return (
        <div ref={ref} className="relative">
            {isOpen && (
                <div
                    // Opens upward: this is the last thing on the page, so there
                    // is nothing below it to open into.
                    className="absolute bottom-full left-0 z-10 mb-2 flex min-w-[200px] flex-col gap-1 rounded-[5px] bg-giphyDarkestGrey p-5 pb-2 shadow-[0_2px_20px_0_rgba(0,0,0,0.5)]"
                    role="group"
                    aria-label={label}
                >
                    {options.map((option) => (
                        <div key={option.value} className="flex items-center justify-between gap-6">
                            <span className="text-[15px] leading-[22px] font-bold text-giphyWhite" lang={option.value}>
                                {option.label}
                            </span>
                            <Radio
                                group="footer-locale"
                                value={option.value}
                                isChecked={option.value === activeLocale}
                                onChange={() => choose(option.value)}
                            />
                        </div>
                    ))}
                </div>
            )}

            <form ref={formRef} action="/api/locale" method="post">
                <input type="hidden" name="returnPath" value={returnPath} />
                <input ref={localeInputRef} type="hidden" name="locale" defaultValue={activeLocale} />
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-expanded={isOpen}
                    aria-label={label}
                    className={cn(
                        'flex cursor-pointer items-center gap-1.5 rounded-[5px] bg-giphyDarkestGrey px-[14px] py-1',
                        'text-[14px] leading-[normal] text-giphyLightGrey transition-colors hover:text-giphyWhite'
                    )}
                >
                    <span lang={active?.value}>{active?.label}</span>
                    {/* Points up while the panel is open, matching the design. */}
                    <svg
                        width="10"
                        height="6"
                        viewBox="0 0 10 6"
                        aria-hidden="true"
                        className={cn('transition-transform', isOpen ? 'rotate-180' : '')}
                    >
                        <path d="M1 1.5L5 5L9 1.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                    </svg>
                </button>
                {/* Selecting submits when JS is available; this is the fallback. */}
                <noscript>
                    <button type="submit" className="sr-only">
                        {label}
                    </button>
                </noscript>
            </form>
        </div>
    )
}

export default FooterLocaleSwitcher
